import React, { useState } from 'react';
import { useThemeContext } from '../../contexts/ThemeContext';
import { defaultTheme } from '../../config/theme';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';

// Presets pra teste rápido das 4 lojas de demo + o default Reviter.
// Valores casam com reviter-api/seeders/003-base-stores.js (THEMES) —
// todos auditados pra passar WCAG AA (white sobre primary ≥4.5:1).
const PRESETS = {
  'Reviter (default AAA)': defaultTheme,
  'Fornecedor ABC (vermelho/âmbar)': {
    primary: '#B91C1C', primaryDark: '#991B1B', primaryLight: '#F87171',
    secondary: '#B45309', secondaryDark: '#92400E', secondaryLight: '#FBBF24',
    accent: '#059669',
    bg: '#FFFFFF', surface: '#FEF2F2', surfaceHover: '#FEE2E2',
    text: '#0F172A', textSecondary: '#4B5563', border: '#FECACA',
    success: '#059669', warning: '#D97706', error: '#DC2626', info: '#2563EB',
  },
  'Loja Seller (roxo/rosa)': {
    primary: '#7C3AED', primaryDark: '#6D28D9', primaryLight: '#A78BFA',
    secondary: '#BE185D', secondaryDark: '#9D174D', secondaryLight: '#F472B6',
    accent: '#059669',
    bg: '#FFFFFF', surface: '#FAF5FF', surfaceHover: '#F3E8FF',
    text: '#0F172A', textSecondary: '#4B5563', border: '#E9D5FF',
    success: '#059669', warning: '#D97706', error: '#DC2626', info: '#2563EB',
  },
  'Loja Catalog (ciano/azul)': {
    primary: '#0E7490', primaryDark: '#155E75', primaryLight: '#22D3EE',
    secondary: '#1D4ED8', secondaryDark: '#1E40AF', secondaryLight: '#60A5FA',
    accent: '#059669',
    bg: '#FFFFFF', surface: '#F0FDFA', surfaceHover: '#CCFBF1',
    text: '#0F172A', textSecondary: '#4B5563', border: '#A5F3FC',
    success: '#059669', warning: '#D97706', error: '#DC2626', info: '#2563EB',
  },
  'Atacado XYZ (verde/azul)': {
    primary: '#15803D', primaryDark: '#166534', primaryLight: '#4ADE80',
    secondary: '#0369A1', secondaryDark: '#075985', secondaryLight: '#38BDF8',
    accent: '#B45309',
    bg: '#FFFFFF', surface: '#F0FDF4', surfaceHover: '#DCFCE7',
    text: '#0F172A', textSecondary: '#4B5563', border: '#BBF7D0',
    success: '#059669', warning: '#D97706', error: '#DC2626', info: '#2563EB',
  },
};

const TOKENS = [
  { label: 'primary', cssVar: '--color-primary' },
  { label: 'primary-dark', cssVar: '--color-primary-dark' },
  { label: 'primary-light', cssVar: '--color-primary-light' },
  { label: 'secondary', cssVar: '--color-secondary' },
  { label: 'secondary-dark', cssVar: '--color-secondary-dark' },
  { label: 'secondary-light', cssVar: '--color-secondary-light' },
  { label: 'accent', cssVar: '--color-accent' },
  { label: 'success', cssVar: '--color-success' },
  { label: 'warning', cssVar: '--color-warning' },
  { label: 'error', cssVar: '--color-error' },
  { label: 'info', cssVar: '--color-info' },
  { label: 'bg', cssVar: '--color-bg' },
  { label: 'surface', cssVar: '--color-surface' },
  { label: 'surface-hover', cssVar: '--color-surface-hover' },
  { label: 'text', cssVar: '--color-text' },
  { label: 'text-secondary', cssVar: '--color-text-secondary' },
  { label: 'border', cssVar: '--color-border' },
];

const Swatch = ({ label, cssVar }) => {
  const value =
    typeof window !== 'undefined'
      ? getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim()
      : '';
  const isDark = ['primary', 'primary-dark', 'secondary', 'secondary-dark', 'accent', 'success', 'warning', 'error', 'info', 'text'].includes(label);
  return (
    <Card className="p-0 overflow-hidden">
      <div
        className="h-16 flex items-center justify-center"
        style={{ backgroundColor: `var(${cssVar})` }}
      >
        <span
          className="text-sm font-mono font-semibold"
          style={{ color: isDark ? 'white' : '#0F172A' }}
        >
          {value}
        </span>
      </div>
      <div className="p-2 text-center">
        <p className="text-xs font-mono font-semibold text-app-text">{label}</p>
        <p className="text-xs text-app-text-secondary">{cssVar}</p>
      </div>
    </Card>
  );
};

const ThemeDebugPage = () => {
  const { updateTheme } = useThemeContext();
  const [preset, setPreset] = useState('Reviter (default AAA)');

  const apply = (name) => {
    setPreset(name);
    updateTheme(PRESETS[name]);
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-3xl font-extrabold text-app-text mb-2">Tema · Debug</h1>
      <p className="text-app-text-secondary mb-6">
        Swatches ao vivo do tema atual. Troque o preset abaixo pra testar os temas das lojas de
        seed sem precisar de backend.
      </p>

      <Card className="p-4 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-semibold text-app-text">Preset ativo:</span>
          <div className="w-72">
            <Select
              value={preset}
              onChange={(e) => apply(e.target.value)}
              options={Object.keys(PRESETS).map((k) => ({ value: k, label: k }))}
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {Object.keys(PRESETS).map((k) => (
              <Button
                key={k}
                size="sm"
                variant={preset === k ? 'primary' : 'subtle'}
                onClick={() => apply(k)}
              >
                {k.split(' ')[0]}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      <section className="mb-8">
        <h2 className="text-lg font-bold text-app-text mb-3">Tokens de cor</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {TOKENS.map((t) => (
            <Swatch key={t.cssVar} {...t} />
          ))}
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-lg font-bold text-app-text mb-3">Exemplos aplicados</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="p-6 space-y-3">
            <h3 className="font-bold">Botões</h3>
            <div className="flex flex-wrap gap-2">
              <Button variant="primary">Primary</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="accent">Accent</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="danger">Danger</Button>
              <Button variant="subtle">Subtle</Button>
            </div>
          </Card>
          <Card className="p-6 space-y-3">
            <h3 className="font-bold">Badges & status</h3>
            <div className="flex flex-wrap gap-2">
              <Badge color="primary">Primary</Badge>
              <Badge color="secondary">Secondary</Badge>
              <Badge color="success" variant="solid">Success</Badge>
              <Badge color="warning">Warning</Badge>
              <Badge color="error" variant="outline">Error</Badge>
              <Badge color="info" dot>Info</Badge>
            </div>
          </Card>
          <Card className="p-6">
            <h3 className="font-bold mb-3">Form controls</h3>
            <div className="space-y-3">
              <Input label="Email" placeholder="seu@email.com" />
              <Input label="Com erro" error="Campo obrigatório" />
            </div>
          </Card>
          <Card
            className="p-6"
            style={{
              background:
                'linear-gradient(135deg, rgb(var(--color-primary-rgb)), rgb(var(--color-secondary-rgb)))',
            }}
          >
            <h3 className="font-bold text-white mb-1">Hero gradient</h3>
            <p className="text-white/90 text-sm">
              Texto branco sobre primary→secondary. Contraste deve passar AAA.
            </p>
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-bold text-app-text mb-3">Meta</h2>
        <Card className="p-4 text-sm space-y-1 font-mono">
          <p>
            <span className="text-app-text-secondary">host:</span>{' '}
            <strong>{typeof window !== 'undefined' ? window.location.hostname : '—'}</strong>
          </p>
          <p>
            <span className="text-app-text-secondary">API base:</span>{' '}
            <strong>{process.env.REACT_APP_API_URL || '(vazio — usa proxy dev ou same-origin)'}</strong>
          </p>
          <p>
            <span className="text-app-text-secondary">Build NODE_ENV:</span>{' '}
            <strong>{process.env.NODE_ENV}</strong>
          </p>
        </Card>
      </section>
    </div>
  );
};

export default ThemeDebugPage;
